import { useEffect, useRef, useState } from 'preact/hooks';
import { createEditor, type EditorApi } from '../editor/editor';
import { LANGUAGES, languageInfo } from '../languages';
import { canRun, runLabelFor } from '../run/runner';
import { runFailed, runStatus } from '../run/output';
import { specState, SPEC_STATE_LABEL } from '../util/spec';
import type { SpecState } from '../types';
import {
  activeCard,
  activeCode,
  activeSpec,
  editorBridge,
  editorSelection,
  isDark,
  review,
  showToast,
  specBridge,
  streaming,
  ui,
  updateUi,
  type ReviewInfo,
  type WorkspaceTab,
} from '../state/store';
import {
  acceptReview,
  inlineEdit,
  markSpecInStep,
  onCodeReviewChange,
  onEditorChange,
  onSpecChange,
  onSpecReviewChange,
  rejectReview,
  runActiveCode,
  setLanguage,
  specAction,
  stopActiveRun,
  stopStreaming,
} from '../state/actions';
import { OutputPanel } from './OutputPanel';
import { PanelBoundary } from './PanelBoundary';
import {
  IconBook,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCode,
  IconCopy,
  IconDownload,
  IconPlay,
  IconRedo,
  IconSparkles,
  IconStop,
  IconTerminal,
  IconUndo,
  IconX,
} from './Icons';

function download(text: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** A file name from the card title, so downloads are recognizable: "Click Counter" becomes click-counter.html. */
function fileName(title: string, extension: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return `${base || 'cue'}.${extension}`;
}

/** Download what the active tab shows: the code file, or the spec as Markdown. */
function downloadActive(): void {
  const card = activeCard.value;
  if (!card) return;
  if (ui.value.workspaceTab === 'spec') download(activeSpec.value, fileName(`${card.title} spec`, 'md'));
  else download(activeCode.value, fileName(card.title, languageInfo(card.language).extension));
}

function copy(text: string, what: string): void {
  void navigator.clipboard.writeText(text).then(
    () => showToast(`${what} copied.`),
    () => showToast('Could not copy to the clipboard.', 'error'),
  );
}

function InlineEditPrompt() {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const selection = editorSelection.value;
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const scope = selection ? `lines ${selection.fromLine}-${selection.toLine}` : 'the whole file';
  return (
    <form
      class="inline-edit"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) void inlineEdit(text.trim());
      }}
    >
      <IconSparkles size={16} />
      <input
        ref={inputRef}
        type="text"
        value={text}
        placeholder={`Describe the change for ${scope}`}
        aria-label="Edit instruction"
        onInput={(e) => setText((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') updateUi({ inlineEditOpen: false });
        }}
      />
      <button type="submit" class="button primary small" disabled={!text.trim()}>
        Edit
      </button>
      <button type="button" class="icon-button" aria-label="Close" onClick={() => updateUi({ inlineEditOpen: false })}>
        <IconX size={16} />
      </button>
    </form>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function reviewMessage(r: ReviewInfo): { title: string; detail: string } {
  if (r.streaming) return { title: 'Cue is writing a proposal.', detail: 'The editors are read-only until it finishes.' };
  const { code, spec } = r;
  if (code?.newFile) {
    return {
      title: 'New file proposed.',
      detail: spec
        ? 'It comes with a spec. Accept both, or reject to keep the card as it was.'
        : 'Accept it into the editor, or reject it to keep the editor empty.',
    };
  }
  if (code && spec) {
    return {
      title: 'Code and spec changes proposed.',
      detail: `${plural(code.remaining, 'code change')} left${spec.remaining ? `, and ${plural(spec.remaining, 'spec change')} in the Spec tab` : ''}. Decide each one inline, or all at once.`,
    };
  }
  if (spec) {
    return {
      title: 'Spec change proposed.',
      detail: spec.newFile ? 'This card had no spec. Accept it, or reject it.' : `${plural(spec.remaining, 'change')} left. Decide each one inline, or all at once.`,
    };
  }
  if (code) {
    return {
      title: 'Reviewing a proposal.',
      detail: code.remaining > 0 ? `${plural(code.remaining, 'change')} left. Accept or reject each one inline, or decide for all.` : 'No differences from the current file.',
    };
  }
  return { title: 'Reviewing a proposal.', detail: '' };
}

function ReviewBar({ info, tab }: { info: ReviewInfo; tab: WorkspaceTab }) {
  const { title, detail } = reviewMessage(info);
  const here = tab === 'spec' ? info.spec : info.code;
  const bridge = tab === 'spec' ? specBridge : editorBridge;
  return (
    <div class={`review-bar${info.streaming ? ' is-streaming' : ''}`} role="status">
      <span class="review-text">
        <strong>{title}</strong> {detail}
      </span>
      <span class="review-actions">
        {info.streaming ? (
          <button type="button" class="button small" onClick={stopStreaming}>
            <IconStop size={15} /> Stop
          </button>
        ) : (
          <>
            {here && here.remaining > 0 && !here.newFile && (
              <button type="button" class="button small" onClick={() => bridge.current?.nextChunk()}>
                Next change
              </button>
            )}
            <button type="button" class="button small primary" onClick={acceptReview}>
              <IconCheck size={15} /> Accept all
            </button>
            <button type="button" class="button small" onClick={rejectReview}>
              <IconX size={15} /> Reject all
            </button>
          </>
        )}
      </span>
    </div>
  );
}

/** Where the spec stands, and the one or two things worth doing about it. */
function SpecStatus({ state, hasCode }: { state: SpecState; hasCode: boolean }) {
  const busy = streaming.value !== null || review.value !== null;
  const actions: { label: string; run: () => void; primary?: boolean }[] = [];
  if (state === 'empty' && hasCode) actions.push({ label: 'Write a spec from the code', run: () => void specAction('write'), primary: true });
  if (state === 'code-changed') {
    actions.push({ label: 'Update the spec', run: () => void specAction('update'), primary: true });
    actions.push({ label: 'It still fits', run: markSpecInStep });
  }
  if (state === 'spec-changed') {
    actions.push({ label: 'Update the code to match', run: () => void specAction('follow'), primary: true });
    actions.push({ label: 'Mark as in step', run: markSpecInStep });
  }
  if (state === 'unchecked') {
    actions.push({ label: 'Check it against the code', run: () => void specAction('check'), primary: true });
    actions.push({ label: 'Mark as in step', run: markSpecInStep });
  }
  const text =
    state === 'empty'
      ? hasCode
        ? 'No spec yet. Write one, or let Cue draft it from the code.'
        : 'No spec yet. Describe what this card should do, or just start prompting.'
      : SPEC_STATE_LABEL[state];
  return (
    <div class={`spec-status is-${state}`}>
      <span class="spec-state">
        <span class="spec-dot" aria-hidden="true" />
        {text}
      </span>
      {actions.length > 0 && !busy && (
        <span class="spec-actions">
          {actions.map((a) => (
            <button key={a.label} type="button" class={`button small${a.primary ? '' : ' ghost'}`} onClick={a.run}>
              {a.label}
            </button>
          ))}
        </span>
      )}
    </div>
  );
}

function useEditor(hostRef: { current: HTMLDivElement | null }, bridge: { current: EditorApi | null }, create: (host: HTMLDivElement) => EditorApi): void {
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const editor = create(host);
    bridge.current = editor;
    return () => {
      editor.destroy();
      if (bridge.current === editor) bridge.current = null;
    };
  }, []);
}

export function Workspace() {
  const card = activeCard.value;
  const cardId = card?.id;
  const language = card?.language ?? 'python';
  const codeHost = useRef<HTMLDivElement>(null);
  const specHost = useRef<HTMLDivElement>(null);
  const dark = isDark.value;
  const reviewState = review.value;
  const status = runStatus.value;
  const state = ui.value;
  const tab = state.workspaceTab;
  const hasError = runFailed.value;
  const code = activeCode.value;
  const spec = activeSpec.value;

  useEditor(codeHost, editorBridge, (host) =>
    createEditor(
      host,
      { doc: activeCode.value, language: activeCard.value?.language ?? 'python', dark: isDark.value },
      {
        onChange: onEditorChange,
        onSelectionChange: (selection) => {
          editorSelection.value = selection;
        },
        onReviewChange: onCodeReviewChange,
        onRun: () => void runActiveCode(),
        onInlineEdit: () => updateUi({ inlineEditOpen: true }),
      },
    ),
  );
  useEditor(specHost, specBridge, (host) =>
    createEditor(
      host,
      {
        doc: activeSpec.value,
        language: 'markdown',
        dark: isDark.value,
        variant: 'spec',
        placeholder: 'What should this card do? A line on the purpose, then bullets for behavior, inputs and outputs, and edge cases.',
      },
      {
        onChange: onSpecChange,
        onSelectionChange: () => {},
        onReviewChange: onSpecReviewChange,
        onRun: () => void runActiveCode(),
        onInlineEdit: () => {},
      },
    ),
  );

  // Show the active card in both editors when it changes, including a review left pending.
  useEffect(() => {
    const current = activeCard.value;
    const codeEditor = editorBridge.current;
    const specEditor = specBridge.current;
    if (!current || !codeEditor || !specEditor) return;
    const pending = current.review;
    codeEditor.setDoc(activeCode.value);
    specEditor.setDoc(activeSpec.value);
    if (pending) {
      if (activeCode.value !== pending.original) codeEditor.startReview(pending.original, activeCode.value);
      if (activeSpec.value !== pending.originalSpec) specEditor.startReview(pending.originalSpec, activeSpec.value);
    }
    void codeEditor.setLanguage(current.language);
  }, [cardId]);

  useEffect(() => {
    editorBridge.current?.setTheme(dark);
    specBridge.current?.setTheme(dark);
  }, [dark]);

  // Cue saves as you type, so Ctrl+S downloads the file instead of the browser saving the page.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        downloadActive();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A hidden editor cannot measure itself; do it when its tab is shown.
  useEffect(() => {
    (tab === 'spec' ? specBridge : editorBridge).current?.refresh();
  }, [tab]);

  if (!card) return null;
  const runnable = canRun(language);
  const running = status !== 'idle';
  const specNow = specState({ code, spec, specSync: card.specSync });
  const specReview = reviewState?.spec != null;
  const specDrift = specNow === 'code-changed' || specNow === 'spec-changed';
  const setTab = (next: WorkspaceTab) => updateUi({ workspaceTab: next });
  const active = tab === 'spec' ? specBridge : editorBridge;

  return (
    <section class="workspace" aria-label="Workspace">
      <div class="toolbar">
        <div class="tabs" role="tablist" aria-label="Card documents">
          <button type="button" role="tab" id="tab-code" aria-selected={tab === 'code'} aria-controls="panel-code" class="tab" onClick={() => setTab('code')}>
            <IconCode size={15} /> Code
            {reviewState?.code && tab !== 'code' && <span class="dot dot-ember" aria-label="Changes to review" />}
          </button>
          <button type="button" role="tab" id="tab-spec" aria-selected={tab === 'spec'} aria-controls="panel-spec" class="tab" onClick={() => setTab('spec')}>
            <IconBook size={15} /> Spec
            {tab !== 'spec' && (specReview || specDrift) && (
              <span class={`dot ${specReview ? 'dot-ember' : 'dot-muted'}`} aria-label={specReview ? 'Changes to review' : 'Out of step with the code'} />
            )}
          </button>
        </div>
        {tab === 'code' && (
          <>
            <span class="toolbar-sep" />
            <select class="language-select" aria-label="Language" value={language} onChange={(e) => setLanguage((e.target as HTMLSelectElement).value)}>
              {LANGUAGES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
            </select>
            {runnable &&
              (running ? (
                <button type="button" class="button small danger" onClick={stopActiveRun} title="Stop the run">
                  <IconStop size={15} /> Stop
                </button>
              ) : (
                <button type="button" class="button small primary" onClick={() => void runActiveCode()} title="Run (Ctrl+Enter)">
                  <IconPlay size={15} /> {runLabelFor(language)}
                </button>
              ))}
            <button
              type="button"
              class="button small ghost"
              title="Ask for a change to the selection or the file (Ctrl+K)"
              aria-pressed={state.inlineEditOpen}
              onClick={() => updateUi({ inlineEditOpen: !state.inlineEditOpen })}
            >
              <IconSparkles size={15} /> <span class="label">Edit with AI</span>
            </button>
          </>
        )}
        <div class="spacer" />
        <span class="touch-only">
          <button type="button" class="icon-button" title="Undo" aria-label="Undo" onClick={() => active.current?.undo()}>
            <IconUndo />
          </button>
          <button type="button" class="icon-button" title="Redo" aria-label="Redo" onClick={() => active.current?.redo()}>
            <IconRedo />
          </button>
        </span>
        <button
          type="button"
          class="icon-button"
          title={tab === 'code' ? 'Copy the code' : 'Copy the spec'}
          aria-label={tab === 'code' ? 'Copy the code' : 'Copy the spec'}
          onClick={() => copy(tab === 'code' ? code : spec, tab === 'code' ? 'Code' : 'Spec')}
        >
          <IconCopy />
        </button>
        <button
          type="button"
          class="icon-button"
          title={tab === 'code' ? 'Download the code (Ctrl+S)' : 'Download the spec (Ctrl+S)'}
          aria-label={tab === 'code' ? 'Download the code' : 'Download the spec'}
          onClick={downloadActive}
        >
          <IconDownload />
        </button>
        <button
          type="button"
          class={`button small ghost output-toggle${hasError ? ' has-error' : ''}`}
          title={state.outputOpen ? 'Hide output' : 'Show output'}
          aria-expanded={state.outputOpen}
          onClick={() => updateUi({ outputOpen: !state.outputOpen })}
        >
          <IconTerminal size={15} /> <span class="label">Output</span>
          {state.outputOpen ? <IconChevronDown size={14} /> : <IconChevronUp size={14} />}
        </button>
      </div>
      {reviewState && <ReviewBar info={reviewState} tab={tab} />}
      {tab === 'code' && state.inlineEditOpen && <InlineEditPrompt />}
      {tab === 'spec' && <SpecStatus state={specNow} hasCode={code.trim() !== ''} />}
      <div id="panel-code" role="tabpanel" aria-labelledby="tab-code" class="editor-host" ref={codeHost} hidden={tab !== 'code'} />
      <div id="panel-spec" role="tabpanel" aria-labelledby="tab-spec" class="editor-host spec-host" ref={specHost} hidden={tab !== 'spec'} />
      {state.outputOpen && (
        <PanelBoundary name="output panel">
          <OutputPanel />
        </PanelBoundary>
      )}
    </section>
  );
}
